/**
 * A minimal Newton-Raphson backend for the residual systems that
 * ./residual.ts grounds (see docs/nonlinear-backends.md).
 *
 * This is the AC-power-flow (feasibility) case: solve F(x) = 0 for a square
 * system, where F is the vector of grounded residuals and x the decision
 * variables. Each iteration assembles the residual vector and its Jacobian (both
 * from one forward-mode autodiff pass per residual), solves the linear system
 * J·Δ = −F, and steps x ← x + Δ until the residual norm falls below tolerance.
 *
 * The dense solver here is a stand-in — fine for the small toy systems this
 * prototype targets; a real backend would use the sparsity the grounder already
 * exposes.
 */

import { GroundedResidual, evalJet } from './residual';

export interface NewtonOptions {
  tol?: number;
  maxIterations?: number;
  /** Steps beyond this magnitude are damped, guarding early divergence. */
  maxStep?: number;
}

export interface NewtonResult {
  converged: boolean;
  iterations: number;
  x: Map<string, number>;
  /** ‖F(x)‖∞ at the returned point. */
  residualNorm: number;
  /** ‖F(x)‖∞ after each iteration, for inspecting convergence. */
  history: number[];
}

const infNorm = (v: number[]): number => v.reduce((m, x) => Math.max(m, Math.abs(x)), 0);

/**
 * Solve a dense linear system A·x = b by Gaussian elimination with partial
 * pivoting. `A` is row-major and consumed in place. Throws if singular.
 */
export function denseSolve(A: number[][], b: number[]): number[] {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-14) throw new Error('singular Jacobian');
    [M[col], M[pivot]] = [M[pivot], M[col]];

    const p = M[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = M[r][col] / p;
      if (factor === 0) continue;
      for (let c = col; c <= n; c++) M[r][c] -= factor * M[col][c];
    }
  }

  // Full elimination above and below leaves a diagonal system.
  return M.map((row, i) => row[n] / row[i]);
}

/**
 * Newton-Raphson on a square residual system.
 *
 * `variables` fixes the column order of the Jacobian; the system must be square
 * (one residual per variable). `seed` gives the starting point (defaults to 0
 * for any variable it omits — pair it with a flat start, V = 1 / θ = 0).
 */
export function newton(
  residuals: GroundedResidual[],
  variables: string[],
  seed: ReadonlyMap<string, number>,
  options: NewtonOptions = {},
): NewtonResult {
  const { tol = 1e-10, maxIterations = 50, maxStep = 10 } = options;

  if (residuals.length !== variables.length) {
    throw new Error(
      `system is not square: ${residuals.length} residuals, ${variables.length} variables`,
    );
  }

  const x = new Map<string, number>(variables.map((v) => [v, seed.get(v) ?? 0]));
  const index = new Map(variables.map((v, i) => [v, i]));
  const history: number[] = [];

  for (let iter = 0; iter < maxIterations; iter++) {
    const F: number[] = [];
    const J: number[][] = [];
    for (const r of residuals) {
      const jet = evalJet(r.node, x);
      F.push(jet.value);
      const row = new Array(variables.length).fill(0);
      for (const [name, g] of jet.grad) {
        const j = index.get(name);
        if (j !== undefined) row[j] = g;
      }
      J.push(row);
    }

    const norm = infNorm(F);
    history.push(norm);
    if (norm < tol) {
      return { converged: true, iterations: iter, x, residualNorm: norm, history };
    }

    const negF = F.map((f) => -f);
    const delta = denseSolve(J, negF);

    // Damp an overlong step so an early iterate can't shoot off.
    const stepNorm = infNorm(delta);
    const scale = stepNorm > maxStep ? maxStep / stepNorm : 1;
    for (const v of variables) x.set(v, x.get(v)! + scale * delta[index.get(v)!]);
  }

  // One last residual evaluation at the final point.
  const finalNorm = infNorm(residuals.map((r) => evalJet(r.node, x).value));
  history.push(finalNorm);
  return {
    converged: finalNorm < tol,
    iterations: maxIterations,
    x,
    residualNorm: finalNorm,
    history,
  };
}
